// Beyond Identity SDK error names (from bi-embedded.js constructors)
const SDK_DATABASE_ERRORS = /databasenotfound|databaseconnectionerror|databaseresulterror/i;
const SDK_DATABASE_MSG = /indexeddb|database|idb/i;

// Browser-native WebAssembly errors
const WASM_NAME = /compileerror/i;
const WASM_MSG = /wasm|webassembly/i;

// Webpack dynamic import errors
const CHUNK_MSG = /loading chunk|chunk.*fail|failed to fetch dynamically imported/i;

// WebAuthn API errors (user biometric cancel/failure)
const BIOMETRIC_NAME = /notallowederror|aborterror/i;
// SDK wraps WebAuthn errors: "HandleUrl(BiAuthenticate(UaAuthenticate(...The operation either timed out or was not allowed..."
const BIOMETRIC_MSG = /biometric|fingerprint|face.*id|touch.*id|user.*cancel|authentication.*cancel|timed out or was not allowed/i;

const CATEGORY_TO_REASON = {
    ChunkLoad: 'sdk:chunk',
    WASMInit: 'sdk:wasm',
    SDKDatabase: 'sdk:database',
    Biometric: 'biometric',
    SDK: 'sdk',
    Config: 'config',
    API: 'api'
};

// Categories where the operation does not add meaningful context
const OPERATION_AGNOSTIC = new Set(['sdk:chunk', 'sdk:wasm', 'sdk:database', 'biometric', 'config']);

/**
 * Builds the prop28 reason suffix from a classifier category and the operation that failed.
 * Generic SDK/API categories are refined by operation (e.g. 'sdk:init', 'api:login').
 * Specific categories (database, wasm, chunk, biometric, config) are returned as-is.
 * Returns null when category is unknown — caller should emit 'passkey:untracked'.
 * @param {string} category - Output of classifyPasskeyError
 * @param {string} [operation] - The passkey operation that failed (init, authenticate, bind, login, remove, etc.)
 * @returns {string|null}
 */
const buildPasskeyReason = (category, operation) => {
    const base = CATEGORY_TO_REASON[category];

    if (!base) {
        return null;
    }

    if (OPERATION_AGNOSTIC.has(base)) {
        return base;
    }

    return operation ? `${base}:${operation}` : base;
};

const classifyPasskeyError = error => {
    const msg = (error?.message || error?.errorMessage || '').toLowerCase();
    const name = (error?.name || '').toLowerCase();
    const combined = `${name} ${msg}`;

    if (SDK_DATABASE_ERRORS.test(name) || SDK_DATABASE_MSG.test(msg)) {
        return 'SDKDatabase';
    }

    if (WASM_NAME.test(name) || WASM_MSG.test(combined)) {
        return 'WASMInit';
    }

    if (CHUNK_MSG.test(msg) || name === 'chunkloaderror') {
        return 'ChunkLoad';
    }

    if (BIOMETRIC_NAME.test(name) || BIOMETRIC_MSG.test(msg)) {
        return 'Biometric';
    }

    if (error?.errorCode === 'PASSKEY_CONFIG_MISSING') {
        return 'Config';
    }

    return 'SDK';
};

const getBuildId = () => {
    const info = Sephora?.buildInfo;

    return `${info?.BUILD_NUMBER || '?'}.${(info?.GIT_COMMIT || '?').substring(0, 7)}`;
};

const logPasskeyForDynatrace = (category, operation, error, flow) => {
    const code = error?.errorCode || error?.code || error?.name || 'UNKNOWN';
    const msg = (error?.message || error?.errorMessage || 'Unknown error').substring(0, 200);
    const build = getBuildId();
    const message = `Passkey_${category}_${operation},c:${code},m:${msg},f:${flow},b:${build}`;

    Sephora?.logger?.error?.(message);

    // Passkey errors are caught and swallowed, so they never reach window.onerror and
    // console.error is not captured by the Dynatrace RUM agent by default. Report the
    // error explicitly via the RUM JS API so it lands in Dynatrace (user.events).
    if (typeof window !== 'undefined') {
        window.dtrum?.reportError?.(message);
    }
};

export {
    classifyPasskeyError, buildPasskeyReason, logPasskeyForDynatrace
};
